import React, { useState } from 'react';
import html2canvas from 'html2canvas';
import { API_BASE_URL } from '../config/api';

const THEME = {
  primary: '#2563eb',
  secondary: '#10b981',
  accent: '#f59e0b',
  textDark: '#1f2937',
  textLight: '#ffffff',
  textMuted: '#6b7280',
  borderLight: '#e5e7eb',
  bgLight: '#f9fafb',
  danger: '#ef4444'
};

const VideoList = ({ onClose, onExpandChange, gameInstanceId }) => {
  const [videos, setVideos] = useState([]);
  const [isStitching, setIsStitching] = useState(false);
  const [stitchedVideoUrl, setStitchedVideoUrl] = useState(null);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [error, setError] = useState(null);
  const [manualPath, setManualPath] = useState('');
  const [youtubeTitle, setYoutubeTitle] = useState('');
  const [youtubeDescription, setYoutubeDescription] = useState('');
  const [youtubeTags, setYoutubeTags] = useState('');
  const [youtubeThumbnails, setYoutubeThumbnails] = useState([]);
  const [collectedDialogue, setCollectedDialogue] = useState('');
  const [showMetadata, setShowMetadata] = useState(false);
  const [expandedVideos, setExpandedVideos] = useState({});
  const [isUploading, setIsUploading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);
  const [recipes, setRecipes] = useState([]);
  const [selectedRecipeId, setSelectedRecipeId] = useState('');
  const [isLoadingRecipes, setIsLoadingRecipes] = useState(false);
  const [currentRecipeId, setCurrentRecipeId] = useState(null);
  const [playingVideos, setPlayingVideos] = useState({});
  const [isGeneratingThumbnail, setIsGeneratingThumbnail] = useState(false);

  // Handle paste events for images
  React.useEffect(() => {
    const handlePaste = async (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;

      for (const item of items) {
        if (item.type.startsWith('image/')) {
          e.preventDefault();
          const file = item.getAsFile();
          if (file) {
            await handleImageUpload(file);
          }
          break;
        }
      }
    };

    window.addEventListener('paste', handlePaste);
    return () => window.removeEventListener('paste', handlePaste);
  }, [videos]); // eslint-disable-line react-hooks/exhaustive-deps

  // Listen for charts from BenchmarkCharts component
  React.useEffect(() => {
    const handleAddCharts = (event) => {
      const { charts } = event.detail;

      if (!charts || charts.length === 0) return;

      console.log('[VideoList] Received charts from BenchmarkCharts:', charts);

      // Add each chart as an image to the video list
      const newVideos = charts.map((chart, index) => ({
        id: Date.now() + index,
        mediaType: 'image',
        type: 'image',
        filename: chart.filename,
        fullPath: chart.fullPath,
        url: chart.url,
        title: `Chart: ${chart.filename}`,
        duration: 8,
        voiceoverText: chart.voiceoverText || '',
        metadata: {
          dialogue: '',
          customNote: ''
        }
      }));

      setVideos([...videos, ...newVideos]);
    };

    window.addEventListener('addChartsToVideoStitcher', handleAddCharts);
    return () => window.removeEventListener('addChartsToVideoStitcher', handleAddCharts);
  }, [videos]); // eslint-disable-line react-hooks/exhaustive-deps

  // Helper function to detect and parse video sequence indicators
  const parseVideoSequence = (filename) => {
    // Match patterns like:
    // - "video-1-3.mp4" or "video_1_3.mp4" (dash or underscore separated)
    // - "video-1of3.mp4" or "video_1of3.mp4" (with "of")
    // - "video 1-3.mp4" or "video 1_3.mp4" (space before numbers)
    const patterns = [
      /^(.+?)[-_](\d+)[-_](\d+)(\.[^.]+)$/,     // video-1-3.mp4 or video_1_3.mp4
      /^(.+?)[-_](\d+)of(\d+)(\.[^.]+)$/,       // video-1of3.mp4 or video_1of3.mp4
      /^(.+?)\s+(\d+)[-_](\d+)(\.[^.]+)$/,      // video 1-3.mp4 or video 1_3.mp4
    ];

    for (const pattern of patterns) {
      const match = filename.match(pattern);
      if (match) {
        const baseName = match[1];
        const currentPart = parseInt(match[2], 10);
        const totalParts = parseInt(match[3], 10);
        const extension = match[4];

        // Validate that it's a reasonable sequence
        if (currentPart > 0 && currentPart <= totalParts && totalParts <= 10) {
          return {
            baseName,
            currentPart,
            totalParts,
            extension,
            pattern: pattern
          };
        }
      }
    }

    return null;
  };

  // Helper function to generate filename for a specific part number
  const generateSequenceFilename = (sequenceInfo, partNumber, originalFilename) => {
    // Determine which pattern was used
    const patternIndex = [
      /^(.+?)[-_](\d+)[-_](\d+)(\.[^.]+)$/,
      /^(.+?)[-_](\d+)of(\d+)(\.[^.]+)$/,
      /^(.+?)\s+(\d+)[-_](\d+)(\.[^.]+)$/,
    ].findIndex(p => p.test(originalFilename));

    const { baseName, totalParts, extension } = sequenceInfo;

    if (patternIndex === 0) {
      // Use same separator as original (dash or underscore)
      const separator = originalFilename.includes('_') && !originalFilename.includes('-') ? '_' : '-';
      return `${baseName}${separator}${partNumber}${separator}${totalParts}${extension}`;
    } else if (patternIndex === 1) {
      // Use "of" format with same separator
      const separator = originalFilename.includes('_') ? '_' : '-';
      return `${baseName}${separator}${partNumber}of${totalParts}${extension}`;
    } else if (patternIndex === 2) {
      // Use space before numbers
      const separator = originalFilename.includes('_') && !originalFilename.includes('-') ? '_' : '-';
      return `${baseName} ${partNumber}${separator}${totalParts}${extension}`;
    }

    return null;
  };

  const handleDrop = async (e) => {
    e.preventDefault();

    // Check for file drops from Finder/Explorer
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      console.log('[VideoList] File drop detected:', files.length, 'files');

      for (let i = 0; i < files.length; i++) {
        const file = files[i];

        // Check if it's a video file
        if (file.type.startsWith('video/')) {
          console.log('[VideoList] Processing video file:', file.name, file.type);

          // Upload the file to the server
          try {
            const formData = new FormData();
            formData.append('video', file);
            formData.append('originalName', file.name);

            const response = await fetch(`${API_BASE_URL}/api/takeGameStep?uploadVideo=true`, {
              method: 'POST',
              body: formData
            });

            const result = await response.json();

            // Parse Lambda response body if needed
            const data = result.body ? JSON.parse(result.body) : result;

            if (data.success && data.video_url) {
              console.log('[VideoList] File uploaded successfully:', data.video_url);

              // Add to video list
              const newVideo = {
                id: Date.now() + i,
                url: data.video_url,
                filename: file.name,
                title: file.name.replace(/\.[^/.]+$/, ''), // Remove extension
                type: 'uploaded_video',
                voiceoverText: '',
                metadata: {
                  uploadedFile: true,
                  originalName: file.name,
                  fileSize: file.size,
                  mimeType: file.type
                }
              };

              setVideos(prev => [...prev, newVideo]);
              console.log('[VideoList] Added uploaded video to list');
            } else {
              alert(`Failed to upload ${file.name}: ${data.error || 'Unknown error'}`);
            }
          } catch (uploadError) {
            console.error('[VideoList] Upload error:', uploadError);
            alert(`Error uploading ${file.name}: ${uploadError.message}`);
          }
        } else if (file.type.startsWith('image/')) {
          // Handle image files
          console.log('[VideoList] Processing image file:', file.name, file.type);
          await handleImageUpload(file);
        } else {
          console.warn('[VideoList] Skipping unsupported file type:', file.name, file.type);
        }
      }

      return; // Don't process as JSON data
    }

    // Check for JSON video data (existing functionality)
    const data = e.dataTransfer.getData('video-data');
    console.log('[VideoList] Drop received, data:', data);
    if (data) {
      try {
        const videoData = JSON.parse(data);
        console.log('[VideoList] Parsed video data:', videoData);

        // Always allow adding videos, even if they already exist (for reuse in sequences)
        console.log('[VideoList] Adding video to list (current count:', videos.length, ')');
        const newVideos = [{
          ...videoData,
          id: Date.now(),
          voiceoverText: videoData.voiceoverText || '' // Initialize voiceover field
        }];

        // Check if this video is part of a sequence
        const sequenceInfo = parseVideoSequence(videoData.filename);
        if (sequenceInfo) {
          console.log('[VideoList] Detected video sequence:', sequenceInfo);

          // Extract the directory path from fullPath or url
          let directoryPath = '';
          if (videoData.fullPath) {
            const pathParts = videoData.fullPath.split('/');
            pathParts.pop(); // Remove filename
            directoryPath = pathParts.join('/');
          } else if (videoData.url) {
            // Extract from URL (e.g., http://localhost:4000/dev/media/dialogue-videos/video.mp4)
            const urlObj = new URL(videoData.url);
            const pathParts = urlObj.pathname.split('/');
            pathParts.pop(); // Remove filename
            directoryPath = pathParts.join('/').replace('/media/', ''); // Remove /media/ prefix
          }

          console.log('[VideoList] Directory path:', directoryPath);

          // Generate all other parts in the sequence
          for (let partNum = 1; partNum <= sequenceInfo.totalParts; partNum++) {
            // Skip the part we already added
            if (partNum === sequenceInfo.currentPart) continue;

            const filename = generateSequenceFilename(sequenceInfo, partNum, videoData.filename);
            if (!filename) continue;

            const fullPath = directoryPath ? `${directoryPath}/${filename}` : filename;
            const url = `${API_BASE_URL}/media/${fullPath}`;

            // Check if this video is already in the list
            const alreadyExists = videos.some(v => v.filename === filename) ||
                                 newVideos.some(v => v.filename === filename);

            if (!alreadyExists) {
              console.log('[VideoList] Auto-adding sequence part:', filename);
              newVideos.push({
                ...videoData,
                id: Date.now() + partNum,
                filename,
                fullPath,
                url,
                title: filename,
                voiceoverText: videoData.voiceoverText || ''
              });
            }
          }

          // Sort videos by part number to maintain sequence order
          newVideos.sort((a, b) => {
            const aSeq = parseVideoSequence(a.filename);
            const bSeq = parseVideoSequence(b.filename);
            if (aSeq && bSeq) {
              return aSeq.currentPart - bSeq.currentPart;
            }
            return 0;
          });

          console.log('[VideoList] Adding', newVideos.length, 'videos from sequence');
        }

        console.log('[VideoList] Final newVideos array:', newVideos);
        setVideos([...videos, ...newVideos]);
        console.log('[VideoList] Videos state updated');
      } catch (err) {
        console.error('Error parsing drop data:', err);
      }
      return;
    }

    // Check for chart drag (NEW)
    const chartDrag = e.dataTransfer.getData('chart-drag');
    if (chartDrag === 'true' && window.draggedChartElement) {
      try {
        console.log('[VideoList] Chart dropped, capturing screenshot...');
        setIsUploading(true);

        const canvas = await html2canvas(window.draggedChartElement, {
          backgroundColor: '#1a1a2e',
          scale: 2, // Higher quality
          logging: false,
          useCORS: true, // Handle cross-origin resources
          allowTaint: false, // Prevent tainted canvas
        });

        console.log('[VideoList] Screenshot captured, resizing to 1280x720...');

        // Create a new canvas at exactly 1280x720 and draw the captured canvas onto it
        const resizedCanvas = document.createElement('canvas');
        resizedCanvas.width = 1280;
        resizedCanvas.height = 720;
        const ctx = resizedCanvas.getContext('2d');

        // Fill with dark background
        ctx.fillStyle = '#1a1a2e';
        ctx.fillRect(0, 0, 1280, 720);

        // Draw the captured canvas, scaled to fit within 1280x720
        const scale = Math.min(1280 / canvas.width, 720 / canvas.height);
        const scaledWidth = canvas.width * scale;
        const scaledHeight = canvas.height * scale;
        const x = (1280 - scaledWidth) / 2;
        const y = (720 - scaledHeight) / 2;

        ctx.drawImage(canvas, x-5, y-5, scaledWidth, scaledHeight);

        console.log('[VideoList] Resized from', canvas.width, 'x', canvas.height, 'to 1280x720, converting to blob...');

        // Convert resized canvas to blob - wrap in Promise to properly await
        const blob = await new Promise((resolve) => {
          resizedCanvas.toBlob(resolve, 'image/png');
        });

        if (blob) {
          console.log('[VideoList] Blob created, creating file...');
          const chartName = window.draggedChartName || 'chart';
          const file = new File([blob], `${chartName}.png`, { type: 'image/png' });

          console.log('[VideoList] File created, uploading...');

          // Upload using existing image upload handler (this adds to videos array)
          await handleImageUpload(file);

          console.log('[VideoList] Chart upload complete!');
        }

        // Clean up global references
        delete window.draggedChartElement;
        delete window.draggedChartName;
      } catch (err) {
        console.error('[VideoList] Error handling chart drop:', err);
        setError('Failed to add chart to video list');
        setIsUploading(false);
      }
      return;
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  const handleRemove = (id) => {
    setVideos(videos.filter(v => v.id !== id));
  };

  const handleReorderDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleReorderDragOver = (e, index) => {
    e.preventDefault();

    if (draggedIndex === null || draggedIndex === index) return;

    const newVideos = [...videos];
    const draggedItem = newVideos[draggedIndex];

    newVideos.splice(draggedIndex, 1);
    newVideos.splice(index, 0, draggedItem);

    setVideos(newVideos);
    setDraggedIndex(index);
  };

  const handleReorderDragEnd = () => {
    setDraggedIndex(null);
  };

  const toggleVideoExpanded = (videoId) => {
    setExpandedVideos(prev => ({
      ...prev,
      [videoId]: !prev[videoId]
    }));
  };

  const updateVideoMetadata = (videoId, field, value) => {
    setVideos(videos.map(v => {
      if (v.id === videoId) {
        return {
          ...v,
          metadata: {
            ...v.metadata,
            [field]: value
          }
        };
      }
      return v;
    }));
  };

  const handleAddManualPath = () => {
    if (!manualPath.trim()) {
      setError('Please enter a file path');
      return;
    }

    // Extract filename from path
    const filename = manualPath.split('/').pop();

    // Check if already in list
    const exists = videos.some(v => v.filename === filename);
    if (exists) {
      setError('This video is already in the list');
      return;
    }

    // Add to list with empty metadata that can be filled in
    const videoData = {
      url: `${API_BASE_URL}/media/${manualPath}`,
      filename: filename,
      fullPath: manualPath, // Store the full path for backend stitching
      title: filename,
      type: 'manual',
      id: Date.now(),
      voiceoverText: '', // Initialize voiceover field
      metadata: {
        dialogue: '',
        playerName: '',
        message: '',
        customNote: ''
      }
    };

    setVideos([...videos, videoData]);
    setManualPath('');
    setError(null);

    // Auto-expand the newly added video so user can add metadata
    setExpandedVideos(prev => ({
      ...prev,
      [videoData.id]: true
    }));
  };

  const handleImageUpload = async (file) => {
    console.log('[handleImageUpload] START - file:', file.name, file.type, file.size);
    setIsUploading(true);
    setError(null);

    try {
      // Validate file type
      const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        throw new Error('Invalid image type. Supported: PNG, JPG, GIF, WebP');
      }
      console.log('[handleImageUpload] File type validated');

      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        throw new Error('Image too large. Maximum size: 10MB');
      }
      console.log('[handleImageUpload] File size validated');

      // Convert to base64
      console.log('[handleImageUpload] Converting to base64...');
      const reader = new FileReader();
      const base64Promise = new Promise((resolve, reject) => {
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
      });
      reader.readAsDataURL(file);
      const base64Data = await base64Promise;
      console.log('[handleImageUpload] Base64 conversion complete, length:', base64Data.length);

      // Upload to backend
      console.log('[handleImageUpload] Uploading to:', `${API_BASE_URL}/api/uploadImage`);

      const response = await fetch(`${API_BASE_URL}/api/uploadImage`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          imageData: base64Data,
          filename: file.name
        })
      });

      console.log('[handleImageUpload] Upload response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json();
        console.error('[handleImageUpload] Upload failed with error:', errorData);
        throw new Error(errorData.error || 'Upload failed');
      }

      const result = await response.json();
      console.log('[handleImageUpload] Upload result:', result);

      // Parse the body if it's a string (API Gateway format)
      const uploadData = typeof result.body === 'string' ? JSON.parse(result.body) : result;
      console.log('[handleImageUpload] Parsed upload data:', uploadData);

      // Add to video list
      const imageData = {
        id: Date.now(),
        mediaType: 'image',
        type: 'image',
        filename: uploadData.filename,
        fullPath: uploadData.fullPath,
        url: uploadData.url,
        title: file.name,
        duration: 8, // default duration
        voiceoverText: '', // Voiceover text for TTS
        metadata: {
          dialogue: '',
          customNote: ''
        }
      };

      console.log('[handleImageUpload] Created imageData:', imageData);
      setVideos(prevVideos => {
        const newVideos = [...prevVideos, imageData];
        console.log('[handleImageUpload] Added to videos array. New length:', newVideos.length);
        return newVideos;
      });

      // Auto-expand for metadata editing
      setExpandedVideos(prev => ({
        ...prev,
        [imageData.id]: true
      }));

      console.log('[handleImageUpload] COMPLETE - Success!');

    } catch (err) {
      console.error('[VideoList] Error uploading image:', err);
      setError(err.message || 'Failed to upload image');
    } finally {
      setIsUploading(false);
    }
  };

  const handleStitch = async () => {
    if (videos.length < 2) {
      setError('Add at least 2 videos to stitch');
      return;
    }

    setIsStitching(true);
    setError(null);
    setStitchedVideoUrl(null);

    try {
      console.log('[VideoList] Starting stitch with videos:', videos);

      // Filter out any malformed images that don't have url/filename
      const validVideos = videos.filter(v => {
        if (v.mediaType === 'image' && !v.url && !v.filename && !v.fullPath) {
          console.error('[VideoList] Skipping malformed image without url/filename:', v);
          return false;
        }
        return true;
      });

      console.log('[VideoList] Sending videos to stitch:', validVideos);

      const requestBody = {
        videos: validVideos.map(v => ({
          url: v.url,
          filename: v.filename,
          fullPath: v.fullPath, // Include full path for manual videos
          mediaType: v.mediaType, // Include mediaType for images
          duration: v.duration, // Include duration for images
          voiceoverText: v.voiceoverText, // Include voiceover text for TTS
          metadata: v.metadata // Include all metadata for AI processing
        }))
      };

      // Include gameInstanceId for thumbnail generation if available
      if (gameInstanceId) {
        requestBody.gameInstanceId = gameInstanceId;
        console.log('[VideoList] Including gameInstanceId for thumbnail generation:', gameInstanceId);
      }

      const response = await fetch(`${API_BASE_URL}/api/stitchVideos`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });

      console.log('[VideoList] Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[VideoList] Error response:', errorText);
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch (e) {
          throw new Error(`Server error: ${response.status} - ${errorText}`);
        }
        throw new Error(errorData.error || 'Failed to stitch videos');
      }

      let result = await response.json();
      console.log('[VideoList] Raw stitch result:', result);

      // Handle Lambda response format (body is a JSON string)
      if (result.body && typeof result.body === 'string') {
        result = JSON.parse(result.body);
        console.log('[VideoList] Parsed body:', result);
      }

      if (result.success && result.videoUrl) {
        console.log('[VideoList] Setting stitched video URL:', result.videoUrl);
        setStitchedVideoUrl(result.videoUrl);

        // Store YouTube metadata if generated
        if (result.youtubeTitle) {
          setYoutubeTitle(result.youtubeTitle);
          setShowMetadata(true);
        }
        if (result.youtubeDescription) {
          setYoutubeDescription(result.youtubeDescription);
        }
        if (result.youtubeTags) {
          setYoutubeTags(result.youtubeTags);
        }
        if (result.youtubeThumbnailUrl) {
          // Stitch endpoint returns single thumbnail for backwards compatibility
          setYoutubeThumbnails([{ url: result.youtubeThumbnailUrl }]);
          console.log('[VideoList] YouTube thumbnail generated:', result.youtubeThumbnailUrl);
        }
        if (result.collectedDialogue) {
          setCollectedDialogue(result.collectedDialogue);
        }
      } else {
        console.error('[VideoList] Invalid result:', result);
        throw new Error('Invalid response from server');
      }
    } catch (err) {
      console.error('[VideoList] Error stitching videos:', err);
      setError(err.message || 'Failed to stitch videos');
    } finally {
      setIsStitching(false);
      console.log('[VideoList] Stitch process complete');
    }
  };

  const handleGenerateThumbnail = async () => {
    if (!gameInstanceId) {
      setError('Game instance ID is required to generate thumbnail');
      return;
    }

    setIsGeneratingThumbnail(true);
    setError(null);

    try {
      console.log('[VideoList] Generating thumbnail for game:', gameInstanceId);

      // Build video metadata from current videos list
      const videoMetadata = videos.map(v => ({
        metadata: v.metadata,
        voiceoverText: v.voiceoverText
      }));

      const requestBody = {
        gameInstanceId,
        recipeId: currentRecipeId,
        videoMetadata
      };

      const response = await fetch(`${API_BASE_URL}/api/generateYoutubeThumbnail`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });

      console.log('[VideoList] Thumbnail response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[VideoList] Thumbnail error response:', errorText);
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch (e) {
          throw new Error(`Server error: ${response.status} - ${errorText}`);
        }
        throw new Error(errorData.error || 'Failed to generate thumbnail');
      }

      let result = await response.json();
      console.log('[VideoList] Thumbnail result:', result);

      // Handle Lambda response format
      if (result.body && typeof result.body === 'string') {
        result = JSON.parse(result.body);
      }

      if (result.success && result.thumbnails) {
        // Filter to only successful thumbnails
        const successfulThumbnails = result.thumbnails.filter(t => t.url);
        setYoutubeThumbnails(successfulThumbnails);
        console.log('[VideoList] Generated thumbnails:', successfulThumbnails.length);

        // Show concepts info
        if (result.concepts) {
          console.log('[VideoList] Thumbnail concepts:', result.concepts);
        }
      } else if (result.success && result.thumbnailUrl) {
        // Backwards compatibility with single thumbnail
        setYoutubeThumbnails([{ url: result.thumbnailUrl, concept: result.concept }]);
        console.log('[VideoList] Thumbnail generated:', result.thumbnailUrl);
      } else {
        throw new Error(result.error || 'Invalid response from server');
      }
    } catch (err) {
      console.error('[VideoList] Error generating thumbnail:', err);
      setError(err.message || 'Failed to generate thumbnail');
    } finally {
      setIsGeneratingThumbnail(false);
    }
  };

  // Fetch recipes on component mount
  React.useEffect(() => {
    fetchRecipes();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Notify parent of expand state changes
  React.useEffect(() => {
    if (onExpandChange) {
      onExpandChange(isExpanded);
    }
  }, [isExpanded]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      if (onExpandChange) {
        onExpandChange(false);
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchRecipes = async () => {
    setIsLoadingRecipes(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/getStitchedVideoRecipes`);

      if (!response.ok) {
        throw new Error('Failed to fetch recipes');
      }

      let result = await response.json();
      if (result.body && typeof result.body === 'string') {
        result = JSON.parse(result.body);
      }

      if (result.success && result.recipes) {
        setRecipes(result.recipes);
      }
    } catch (err) {
      console.error('[VideoList] Error fetching recipes:', err);
    } finally {
      setIsLoadingRecipes(false);
    }
  };

  const loadRecipe = async (recipeId) => {
    if (!recipeId) return;

    setIsLoadingRecipes(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/api/loadStitchedVideoRecipe?recipeId=${recipeId}`);

      if (!response.ok) {
        throw new Error('Failed to load recipe');
      }

      let result = await response.json();
      if (result.body && typeof result.body === 'string') {
        result = JSON.parse(result.body);
      }

      if (result.success && result.recipe) {
        const recipe = result.recipe;

        // Load videos with unique IDs
        const loadedVideos = recipe.videos.map((v, index) => ({
          ...v,
          id: Date.now() + index
        }));

        setVideos(loadedVideos);
        setCurrentRecipeId(recipeId);

        // Load metadata if available
        if (recipe.youtubeTitle) setYoutubeTitle(recipe.youtubeTitle);
        if (recipe.youtubeDescription) setYoutubeDescription(recipe.youtubeDescription);
        if (recipe.youtubeTags) setYoutubeTags(recipe.youtubeTags);
        if (recipe.collectedDialogue) setCollectedDialogue(recipe.collectedDialogue);
        if (recipe.outputVideoUrl) setStitchedVideoUrl(recipe.outputVideoUrl);

        console.log(`[VideoList] Loaded recipe ${recipeId} with ${loadedVideos.length} videos`);
      }
    } catch (err) {
      console.error('[VideoList] Error loading recipe:', err);
      setError(err.message || 'Failed to load recipe');
    } finally {
      setIsLoadingRecipes(false);
    }
  };

  const duplicateRecipe = async () => {
    if (!currentRecipeId) {
      setError('No recipe selected to duplicate');
      return;
    }

    setIsLoadingRecipes(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/api/duplicateStitchedVideoRecipe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ recipeId: currentRecipeId })
      });

      if (!response.ok) {
        throw new Error('Failed to duplicate recipe');
      }

      let result = await response.json();
      if (result.body && typeof result.body === 'string') {
        result = JSON.parse(result.body);
      }

      if (result.success && result.recipeId) {
        // Refresh recipes list
        await fetchRecipes();

        // Load the new copy
        await loadRecipe(result.recipeId);
        setSelectedRecipeId(result.recipeId);

        // Clear the stitched video URL since this is a new copy
        setStitchedVideoUrl(null);

        console.log(`[VideoList] Duplicated recipe to ${result.recipeId}`);
      }
    } catch (err) {
      console.error('[VideoList] Error duplicating recipe:', err);
      setError(err.message || 'Failed to duplicate recipe');
    } finally {
      setIsLoadingRecipes(false);
    }
  };

  const toggleVideoPlayback = (videoId, videoElement) => {
    if (playingVideos[videoId]) {
      videoElement.pause();
      setPlayingVideos(prev => ({ ...prev, [videoId]: false }));
    } else {
      videoElement.muted = false; // Unmute when playing
      videoElement.play();
      setPlayingVideos(prev => ({ ...prev, [videoId]: true }));
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      right: 0,
      width: isExpanded ? '550px' : '50px',
      height: '100vh',
      backgroundColor: THEME.textLight,
      borderLeft: `2px solid ${THEME.primary}`,
      boxShadow: isExpanded ? '-4px 0 12px rgba(0, 0, 0, 0.15)' : '-2px 0 8px rgba(0, 0, 0, 0.1)',
      display: 'flex',
      flexDirection: 'column',
      zIndex: 1000,
      transition: 'width 0.3s ease, box-shadow 0.3s ease'
    }}>
      {/* Header */}
      <div style={{
        padding: '12px 16px',
        backgroundColor: THEME.primary,
        color: THEME.textLight,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexShrink: 0
      }}>
        {isExpanded ? (
          <>
            <div style={{ fontWeight: 'bold', fontSize: '16px' }}>
              🎬 Video Stitcher ({videos.length})
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => setIsExpanded(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: THEME.textLight,
                  fontSize: '20px',
                  cursor: 'pointer',
                  padding: '0 4px'
                }}
                title="Collapse"
              >
                ◀
              </button>
              <button
                onClick={onClose}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: THEME.textLight,
                  fontSize: '20px',
                  cursor: 'pointer',
                  padding: '0 4px'
                }}
                title="Close"
              >
                ×
              </button>
            </div>
          </>
        ) : (
          <button
            onClick={() => setIsExpanded(true)}
            style={{
              background: 'transparent',
              border: 'none',
              color: THEME.textLight,
              fontSize: '20px',
              cursor: 'pointer',
              padding: '0',
              width: '100%',
              display: 'flex',
              justifyContent: 'center'
            }}
            title="Expand Video Stitcher"
          >
            ▶
          </button>
        )}
      </div>

      {/* Recipe Selector */}
      {isExpanded && (
      <div style={{
        padding: '12px 16px',
        borderBottom: `1px solid ${THEME.borderLight}`,
        backgroundColor: THEME.bgLight
      }}>
        <div style={{
          fontSize: '12px',
          color: THEME.textMuted,
          marginBottom: '6px',
          fontWeight: '500'
        }}>
          Load Previous Stitch:
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <select
            value={selectedRecipeId}
            onChange={(e) => {
              setSelectedRecipeId(e.target.value);
              loadRecipe(e.target.value);
            }}
            disabled={isLoadingRecipes}
            style={{
              flex: 1,
              padding: '6px 10px',
              border: `1px solid ${THEME.borderLight}`,
              borderRadius: '4px',
              fontSize: '13px',
              backgroundColor: 'white',
              cursor: 'pointer'
            }}
          >
            <option value="">-- Select a previous stitch --</option>
            {recipes.map(recipe => (
              <option key={recipe.id} value={recipe.id}>
                {recipe.youtubeTitle || recipe.name} ({recipe.videoCount} videos)
              </option>
            ))}
          </select>
          <button
            onClick={duplicateRecipe}
            disabled={!currentRecipeId || isLoadingRecipes}
            title="Create a copy of current recipe"
            style={{
              padding: '6px 12px',
              backgroundColor: currentRecipeId ? THEME.secondary : THEME.borderLight,
              color: currentRecipeId ? THEME.textLight : THEME.textMuted,
              border: 'none',
              borderRadius: '4px',
              fontSize: '13px',
              cursor: currentRecipeId ? 'pointer' : 'not-allowed',
              fontWeight: '500'
            }}
          >
            📋 Copy
          </button>
        </div>
      </div>
      )}

      {/* Manual Path Input */}
      {isExpanded && (
      <div style={{
        padding: '12px 16px',
        borderBottom: `1px solid ${THEME.borderLight}`,
        backgroundColor: THEME.bgLight
      }}>
        <div style={{
          fontSize: '12px',
          color: THEME.textMuted,
          marginBottom: '6px',
          fontWeight: '500'
        }}>
          Add by file path:
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <input
            type="text"
            value={manualPath}
            onChange={(e) => setManualPath(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleAddManualPath()}
            placeholder="e.g., intro-videos/video.mp4"
            style={{
              flex: 1,
              padding: '6px 10px',
              border: `1px solid ${THEME.borderLight}`,
              borderRadius: '4px',
              fontSize: '13px',
              fontFamily: 'monospace'
            }}
          />
          <button
            onClick={handleAddManualPath}
            disabled={!manualPath.trim()}
            style={{
              padding: '6px 12px',
              backgroundColor: !manualPath.trim() ? THEME.textMuted : THEME.primary,
              color: THEME.textLight,
              border: 'none',
              borderRadius: '4px',
              fontSize: '12px',
              fontWeight: 'bold',
              cursor: !manualPath.trim() ? 'not-allowed' : 'pointer',
              whiteSpace: 'nowrap'
            }}
          >
            + Add
          </button>
        </div>
        <div style={{
          fontSize: '11px',
          color: THEME.textMuted,
          marginTop: '4px'
        }}>
          Path relative to local-storage/ (e.g., botc-videos/intro.mp4)
        </div>
      </div>
      )}

      {/* Upload indicator */}
      {isExpanded && isUploading && (
        <div style={{
          padding: '12px 16px',
          backgroundColor: THEME.bgLight,
          borderBottom: `1px solid ${THEME.borderLight}`,
          fontSize: '13px',
          color: THEME.primary,
          textAlign: 'center',
          fontWeight: '500'
        }}>
          ⏳ Uploading image...
        </div>
      )}

      {/* Drop Zone */}
      {isExpanded && (
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '16px',
          minHeight: '200px',
          backgroundColor: videos.length === 0 ? THEME.bgLight : THEME.textLight
        }}
      >
        {videos.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '40px 20px',
            color: THEME.textMuted,
            fontSize: '14px'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🎥</div>
            <div>Drag videos or images here to create a compilation</div>
            <div style={{ fontSize: '12px', marginTop: '8px' }}>
              You can also paste images (Ctrl/Cmd+V)
            </div>
            <div style={{ fontSize: '12px', marginTop: '4px' }}>
              Videos and images will be stitched in the order they appear
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {videos.map((video, index) => (
              <div
                key={video.id}
                style={{
                  backgroundColor: THEME.textLight,
                  border: `1px solid ${THEME.borderLight}`,
                  borderRadius: '6px',
                  overflow: 'hidden'
                }}
              >
                {/* Video Item Header */}
                <div
                  draggable
                  onDragStart={(e) => handleReorderDragStart(e, index)}
                  onDragOver={(e) => handleReorderDragOver(e, index)}
                  onDragEnd={handleReorderDragEnd}
                  style={{
                    padding: '12px',
                    backgroundColor: draggedIndex === index ? THEME.bgLight : THEME.textLight,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    cursor: 'move',
                    opacity: draggedIndex === index ? 0.5 : 1
                  }}
                >
                  <div style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    backgroundColor: THEME.primary,
                    color: THEME.textLight,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    flexShrink: 0
                  }}>
                    {index + 1}
                  </div>

                  {/* Tiny preview thumbnail */}
                  <div style={{
                    width: '240px',
                    height: '136px',
                    borderRadius: '4px',
                    overflow: 'hidden',
                    backgroundColor: '#000',
                    border: `1px solid ${THEME.borderLight}`,
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative'
                  }}>
                    {video.mediaType === 'image' ? (
                      <img
                        src={video.url}
                        alt={video.filename}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                    ) : (
                      <>
                        <video
                          ref={(el) => {
                            if (el) {
                              el.dataset.videoId = video.id;
                            }
                          }}
                          src={video.url}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                          controls
                          playsInline
                          onClick={(e) => e.stopPropagation()}
                        />
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            const videoEl = e.currentTarget.parentElement.querySelector('video');
                            toggleVideoPlayback(video.id, videoEl);
                          }}
                          style={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: '32px',
                            height: '32px',
                            borderRadius: '50%',
                            backgroundColor: 'rgba(0, 0, 0, 0.6)',
                            color: 'white',
                            border: '2px solid white',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '14px',
                            padding: 0,
                            zIndex: 10
                          }}
                        >
                          {playingVideos[video.id] ? '⏸' : '▶'}
                        </button>
                      </>
                    )}
                  </div>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: '13px',
                      fontWeight: '500',
                      color: THEME.textDark,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {video.title || video.filename || 'Video'}
                    </div>
                    <div style={{
                      fontSize: '11px',
                      color: THEME.textMuted,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <span>
                        {video.mediaType === 'image' ? '🖼️ image' : '🎥 video'}
                        {video.voiceoverText && ' • 🎤 voiceover'}
                        {video.metadata && ' • Has metadata'}
                      </span>

                      {/* Duration input for images */}
                      {video.mediaType === 'image' && (
                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          •
                          <input
                            type="number"
                            value={video.duration || 8}
                            onChange={(e) => {
                              const newDuration = Math.max(1, Math.min(60, parseInt(e.target.value) || 8));
                              setVideos(videos.map(v =>
                                v.id === video.id ? { ...v, duration: newDuration } : v
                              ));
                            }}
                            onClick={(e) => e.stopPropagation()}
                            style={{
                              width: '50px',
                              padding: '2px 4px',
                              border: `1px solid ${THEME.borderLight}`,
                              borderRadius: '3px',
                              fontSize: '11px',
                              textAlign: 'center'
                            }}
                            min="1"
                            max="60"
                          />
                          <span>sec</span>
                        </span>
                      )}
                    </div>

                    {/* Voiceover text input for images */}
                    {video.mediaType === 'image' && (
                      <div style={{
                        marginTop: '8px',
                        padding: '8px',
                        backgroundColor: video.voiceoverText ? '#f0f9ff' : THEME.bgLight,
                        borderRadius: '4px',
                        border: `1px solid ${video.voiceoverText ? '#3b82f6' : THEME.borderLight}`
                      }}>
                        <label style={{
                          display: 'block',
                          fontSize: '10px',
                          fontWeight: 'bold',
                          color: THEME.textMuted,
                          marginBottom: '4px'
                        }}>
                          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            {video.voiceoverText ? '🎤' : '🎙️'} Voiceover (optional):
                          </span>
                        </label>
                        <textarea
                          value={video.voiceoverText || ''}
                          onChange={(e) => {
                            setVideos(videos.map(v =>
                              v.id === video.id ? { ...v, voiceoverText: e.target.value } : v
                            ));
                          }}
                          placeholder="Add text to narrate over this image..."
                          rows={2}
                          style={{
                            width: '100%',
                            padding: '6px',
                            border: `1px solid ${THEME.borderLight}`,
                            borderRadius: '4px',
                            fontSize: '11px',
                            fontFamily: 'inherit',
                            resize: 'vertical',
                            backgroundColor: THEME.textLight
                          }}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <div style={{
                          fontSize: '10px',
                          color: THEME.textMuted,
                          marginTop: '4px'
                        }}>
                          Audio starts 0.5s after video begins. Video duration = audio length + 1s.
                        </div>
                      </div>
                    )}
                  </div>
                  {video.metadata && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleVideoExpanded(video.id);
                      }}
                      style={{
                        background: THEME.secondary,
                        color: THEME.textLight,
                        border: 'none',
                        borderRadius: '4px',
                        padding: '4px 8px',
                        fontSize: '11px',
                        cursor: 'pointer',
                        flexShrink: 0,
                        fontWeight: 'bold'
                      }}
                    >
                      {expandedVideos[video.id] ? '▲ Hide' : '▼ Info'}
                    </button>
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemove(video.id);
                    }}
                    style={{
                      background: THEME.danger,
                      color: THEME.textLight,
                      border: 'none',
                      borderRadius: '4px',
                      padding: '4px 8px',
                      fontSize: '12px',
                      cursor: 'pointer',
                      flexShrink: 0
                    }}
                  >
                    Remove
                  </button>
                </div>

                {/* Expandable Metadata Section */}
                {expandedVideos[video.id] && video.metadata && (
                  <div style={{
                    padding: '12px',
                    backgroundColor: THEME.bgLight,
                    borderTop: `1px solid ${THEME.borderLight}`,
                    fontSize: '12px'
                  }}>
                    <div style={{
                      fontWeight: 'bold',
                      color: THEME.textDark,
                      marginBottom: '8px',
                      fontSize: '11px',
                      textTransform: 'uppercase'
                    }}>
                      Video Metadata {video.type === 'manual' && <span style={{ fontWeight: 'normal', textTransform: 'none', color: THEME.textMuted }}>(editable)</span>}
                    </div>

                    {/* Dialogue - always show for manual videos or if it has content */}
                    {(video.type === 'manual' || video.metadata.dialogue || video.metadata.introSpeech) && (
                      <div style={{ marginBottom: '8px' }}>
                        <label style={{
                          display: 'block',
                          fontSize: '10px',
                          fontWeight: 'bold',
                          color: THEME.textMuted,
                          marginBottom: '4px'
                        }}>
                          Dialogue:
                        </label>
                        <textarea
                          value={video.metadata.dialogue || video.metadata.introSpeech || ''}
                          onChange={(e) => updateVideoMetadata(video.id, 'dialogue', e.target.value)}
                          placeholder={video.type === 'manual' ? 'Add dialogue or description for this video...' : ''}
                          rows={3}
                          style={{
                            width: '100%',
                            padding: '6px',
                            border: `1px solid ${THEME.borderLight}`,
                            borderRadius: '4px',
                            fontSize: '11px',
                            fontFamily: 'inherit',
                            resize: 'vertical'
                          }}
                        />
                      </div>
                    )}

                    {/* Player Name - always show for manual videos or if it has content */}
                    {(video.type === 'manual' || video.metadata.playerName) && (
                      <div style={{ marginBottom: '8px' }}>
                        <label style={{
                          display: 'block',
                          fontSize: '10px',
                          fontWeight: 'bold',
                          color: THEME.textMuted,
                          marginBottom: '4px'
                        }}>
                          Player Name:
                        </label>
                        <input
                          type="text"
                          value={video.metadata.playerName || ''}
                          onChange={(e) => updateVideoMetadata(video.id, 'playerName', e.target.value)}
                          placeholder={video.type === 'manual' ? 'Enter player name...' : ''}
                          style={{
                            width: '100%',
                            padding: '6px',
                            border: `1px solid ${THEME.borderLight}`,
                            borderRadius: '4px',
                            fontSize: '11px'
                          }}
                        />
                      </div>
                    )}

                    {/* Scene Info */}
                    {video.metadata.prompt && video.metadata.prompt.action && (
                      <div style={{ marginBottom: '8px' }}>
                        <label style={{
                          display: 'block',
                          fontSize: '10px',
                          fontWeight: 'bold',
                          color: THEME.textMuted,
                          marginBottom: '4px'
                        }}>
                          Scene Action:
                        </label>
                        <div style={{
                          padding: '6px',
                          backgroundColor: THEME.textLight,
                          border: `1px solid ${THEME.borderLight}`,
                          borderRadius: '4px',
                          fontSize: '11px',
                          color: THEME.textDark
                        }}>
                          {video.metadata.prompt.action}
                        </div>
                      </div>
                    )}

                    {/* Characters */}
                    {video.metadata.prompt && video.metadata.prompt.characters && video.metadata.prompt.characters.length > 0 && (
                      <div style={{ marginBottom: '8px' }}>
                        <label style={{
                          display: 'block',
                          fontSize: '10px',
                          fontWeight: 'bold',
                          color: THEME.textMuted,
                          marginBottom: '4px'
                        }}>
                          Characters:
                        </label>
                        <div style={{
                          padding: '6px',
                          backgroundColor: THEME.textLight,
                          border: `1px solid ${THEME.borderLight}`,
                          borderRadius: '4px',
                          fontSize: '11px',
                          color: THEME.textDark
                        }}>
                          {video.metadata.prompt.characters.join(', ')}
                        </div>
                      </div>
                    )}

                    {/* Message - always show for manual videos or if it has content */}
                    {(video.type === 'manual' || video.metadata.message) && (
                      <div style={{ marginBottom: '8px' }}>
                        <label style={{
                          display: 'block',
                          fontSize: '10px',
                          fontWeight: 'bold',
                          color: THEME.textMuted,
                          marginBottom: '4px'
                        }}>
                          Message/Context:
                        </label>
                        <textarea
                          value={video.metadata.message || ''}
                          onChange={(e) => updateVideoMetadata(video.id, 'message', e.target.value)}
                          placeholder={video.type === 'manual' ? 'Add context or notes about this video...' : ''}
                          rows={2}
                          style={{
                            width: '100%',
                            padding: '6px',
                            border: `1px solid ${THEME.borderLight}`,
                            borderRadius: '4px',
                            fontSize: '11px',
                            fontFamily: 'inherit',
                            resize: 'vertical'
                          }}
                        />
                      </div>
                    )}

                    {/* Custom Note - only for manual videos */}
                    {video.type === 'manual' && (
                      <div style={{ marginBottom: '8px' }}>
                        <label style={{
                          display: 'block',
                          fontSize: '10px',
                          fontWeight: 'bold',
                          color: THEME.textMuted,
                          marginBottom: '4px'
                        }}>
                          Custom Note:
                        </label>
                        <textarea
                          value={video.metadata.customNote || ''}
                          onChange={(e) => updateVideoMetadata(video.id, 'customNote', e.target.value)}
                          placeholder="Any additional information about this video..."
                          rows={2}
                          style={{
                            width: '100%',
                            padding: '6px',
                            border: `1px solid ${THEME.borderLight}`,
                            borderRadius: '4px',
                            fontSize: '11px',
                            fontFamily: 'inherit',
                            resize: 'vertical',
                            fontStyle: 'italic'
                          }}
                        />
                      </div>
                    )}

                    {/* Model */}
                    {video.metadata.model && (
                      <div style={{
                        fontSize: '10px',
                        color: THEME.textMuted,
                        fontStyle: 'italic'
                      }}>
                        Model: {video.metadata.model}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      )}

      {/* Error Message */}
      {isExpanded && error && (
        <div style={{
          padding: '12px 16px',
          backgroundColor: '#fee',
          color: THEME.danger,
          fontSize: '13px',
          borderTop: `1px solid ${THEME.borderLight}`
        }}>
          ⚠️ {error}
        </div>
      )}

      {/* Stitched Video Result */}
      {isExpanded && stitchedVideoUrl && (
        <div style={{
          padding: '16px',
          borderTop: `1px solid ${THEME.borderLight}`,
          backgroundColor: THEME.bgLight
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '8px'
          }}>
            <div style={{
              fontSize: '13px',
              fontWeight: 'bold',
              color: THEME.textDark
            }}>
              ✅ Stitched Video Ready:
            </div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <a
                href={stitchedVideoUrl}
                download={`stitched-video-${Date.now()}.mp4`}
                style={{
                  padding: '4px 12px',
                  backgroundColor: THEME.secondary,
                  color: THEME.textLight,
                  textDecoration: 'none',
                  borderRadius: '4px',
                  fontSize: '12px',
                  fontWeight: 'bold',
                  cursor: 'pointer'
                }}
              >
                ⬇ Download
              </a>
              <button
                onClick={() => setStitchedVideoUrl(null)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: THEME.textMuted,
                  fontSize: '18px',
                  cursor: 'pointer',
                  padding: '0 4px'
                }}
              >
                ×
              </button>
            </div>
          </div>
          <video
            controls
            style={{
              width: '100%',
              borderRadius: '4px',
              maxHeight: '200px'
            }}
            src={stitchedVideoUrl}
          />
        </div>
      )}

      {/* YouTube Thumbnails Grid */}
      {isExpanded && youtubeThumbnails.length > 0 && (
        <div style={{
          padding: '16px',
          borderTop: `1px solid ${THEME.borderLight}`,
          backgroundColor: THEME.bgLight
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '12px'
          }}>
            <div>
              <div style={{
                fontSize: '13px',
                fontWeight: 'bold',
                color: THEME.textDark
              }}>
                🖼️ YouTube Thumbnails ({youtubeThumbnails.length} options)
              </div>
              <div style={{
                fontSize: '10px',
                color: THEME.textMuted,
                marginTop: '2px'
              }}>
                Academic → Engaging → Dramatic → Tabloid
              </div>
            </div>
            <button
              onClick={() => setYoutubeThumbnails([])}
              style={{
                background: 'transparent',
                border: 'none',
                color: THEME.textMuted,
                fontSize: '12px',
                cursor: 'pointer',
                padding: '4px 8px'
              }}
            >
              Clear All ×
            </button>
          </div>

          {/* 2x2 Grid of Thumbnails */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '12px'
          }}>
            {youtubeThumbnails.map((thumbnail, index) => (
              <div key={index} style={{
                border: `1px solid ${THEME.borderLight}`,
                borderRadius: '8px',
                overflow: 'hidden',
                backgroundColor: THEME.bgDark
              }}>
                {/* Thumbnail Image */}
                <img
                  src={thumbnail.url}
                  alt={`Thumbnail Option ${index + 1}`}
                  style={{
                    width: '100%',
                    display: 'block',
                    cursor: 'pointer'
                  }}
                  onClick={() => window.open(thumbnail.url, '_blank')}
                  title="Click to view full size"
                />

                {/* Thumbnail Info & Actions */}
                <div style={{
                  padding: '8px',
                  backgroundColor: THEME.bgLight
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '4px'
                  }}>
                    <div>
                      <span style={{
                        fontSize: '11px',
                        fontWeight: 'bold',
                        color: THEME.textDark
                      }}>
                        {thumbnail.concept?.levelName || `Option ${index + 1}`}
                      </span>
                      {thumbnail.concept?.sensationalismLevel && (
                        <span style={{
                          fontSize: '9px',
                          color: THEME.textMuted,
                          marginLeft: '6px'
                        }}>
                          (Level {thumbnail.concept.sensationalismLevel}/4)
                        </span>
                      )}
                    </div>
                    <a
                      href={thumbnail.url}
                      download={`youtube-thumbnail-${thumbnail.concept?.levelName?.toLowerCase().replace(/\//g, '-') || index + 1}-${Date.now()}.png`}
                      style={{
                        padding: '2px 8px',
                        backgroundColor: THEME.secondary,
                        color: THEME.textLight,
                        textDecoration: 'none',
                        borderRadius: '4px',
                        fontSize: '10px',
                        fontWeight: 'bold',
                        cursor: 'pointer'
                      }}
                    >
                      ⬇ Download
                    </a>
                  </div>

                  {/* Show concept if available */}
                  {thumbnail.concept && (
                    <div style={{
                      fontSize: '10px',
                      color: THEME.textMuted,
                      lineHeight: '1.3',
                      maxHeight: '40px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}
                    title={typeof thumbnail.concept === 'string'
                      ? thumbnail.concept
                      : thumbnail.concept.textOverlay || thumbnail.concept.action || ''}
                    >
                      {(() => {
                        // Handle concept as object or string
                        const conceptText = typeof thumbnail.concept === 'string'
                          ? thumbnail.concept
                          : thumbnail.concept.textOverlay || thumbnail.concept.action || '';
                        return conceptText.length > 80
                          ? conceptText.substring(0, 80) + '...'
                          : conceptText;
                      })()}
                    </div>
                  )}

                  {/* Show characters used if available */}
                  {thumbnail.charactersUsed && thumbnail.charactersUsed.length > 0 && (
                    <div style={{
                      fontSize: '9px',
                      color: THEME.accent,
                      marginTop: '4px'
                    }}>
                      Characters: {thumbnail.charactersUsed.join(', ')}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div style={{
            fontSize: '11px',
            color: THEME.textMuted,
            marginTop: '12px',
            textAlign: 'center'
          }}>
            Click any thumbnail to view full size • Recommended: 1280x720 for YouTube
          </div>
        </div>
      )}

      {/* YouTube Metadata Section */}
      {isExpanded && showMetadata && (
        <div style={{
          padding: '16px',
          borderTop: `1px solid ${THEME.borderLight}`,
          backgroundColor: THEME.textLight
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '12px'
          }}>
            <div style={{
              fontSize: '14px',
              fontWeight: 'bold',
              color: THEME.textDark
            }}>
              📝 YouTube Metadata
            </div>
            <button
              onClick={() => setShowMetadata(false)}
              style={{
                background: 'transparent',
                border: 'none',
                color: THEME.textMuted,
                fontSize: '18px',
                cursor: 'pointer',
                padding: '0 4px'
              }}
            >
              ×
            </button>
          </div>

          {/* YouTube Title */}
          <div style={{ marginBottom: '12px' }}>
            <label style={{
              display: 'block',
              fontSize: '11px',
              fontWeight: 'bold',
              color: THEME.textMuted,
              marginBottom: '4px',
              textTransform: 'uppercase'
            }}>
              Title:
            </label>
            <input
              type="text"
              value={youtubeTitle}
              onChange={(e) => setYoutubeTitle(e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                border: `1px solid ${THEME.borderLight}`,
                borderRadius: '4px',
                fontSize: '13px',
                fontFamily: 'inherit'
              }}
            />
          </div>

          {/* YouTube Description */}
          <div style={{ marginBottom: '12px' }}>
            <label style={{
              display: 'block',
              fontSize: '11px',
              fontWeight: 'bold',
              color: THEME.textMuted,
              marginBottom: '4px',
              textTransform: 'uppercase'
            }}>
              Description:
            </label>
            <textarea
              value={youtubeDescription}
              onChange={(e) => setYoutubeDescription(e.target.value)}
              rows={4}
              style={{
                width: '100%',
                padding: '8px',
                border: `1px solid ${THEME.borderLight}`,
                borderRadius: '4px',
                fontSize: '12px',
                fontFamily: 'inherit',
                resize: 'vertical'
              }}
            />
          </div>

          {/* YouTube Tags */}
          {youtubeTags && (
            <div style={{ marginBottom: '12px' }}>
              <label style={{
                display: 'block',
                fontSize: '11px',
                fontWeight: 'bold',
                color: THEME.textMuted,
                marginBottom: '4px',
                textTransform: 'uppercase'
              }}>
                Tags:
              </label>
              <textarea
                value={youtubeTags}
                onChange={(e) => setYoutubeTags(e.target.value)}
                rows={2}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: `1px solid ${THEME.borderLight}`,
                  borderRadius: '4px',
                  fontSize: '11px',
                  fontFamily: 'inherit',
                  resize: 'vertical'
                }}
              />
              <div style={{
                fontSize: '10px',
                color: THEME.textMuted,
                marginTop: '4px'
              }}>
                Comma-separated tags for YouTube
              </div>
            </div>
          )}

          {/* Collected Dialogue */}
          {collectedDialogue && (
            <div style={{ marginBottom: '12px' }}>
              <label style={{
                display: 'block',
                fontSize: '11px',
                fontWeight: 'bold',
                color: THEME.textMuted,
                marginBottom: '4px',
                textTransform: 'uppercase'
              }}>
                Collected Dialogue:
              </label>
              <textarea
                value={collectedDialogue}
                onChange={(e) => setCollectedDialogue(e.target.value)}
                rows={6}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: `1px solid ${THEME.borderLight}`,
                  borderRadius: '4px',
                  fontSize: '11px',
                  fontFamily: 'monospace',
                  resize: 'vertical',
                  backgroundColor: THEME.bgLight
                }}
              />
            </div>
          )}

          {/* Copy Buttons */}
          <div style={{
            display: 'flex',
            gap: '8px',
            marginTop: '12px',
            flexWrap: 'wrap'
          }}>
            <button
              onClick={() => {
                navigator.clipboard.writeText(youtubeTitle);
                alert('Title copied to clipboard!');
              }}
              style={{
                flex: '1 1 auto',
                padding: '6px',
                backgroundColor: THEME.primary,
                color: THEME.textLight,
                border: 'none',
                borderRadius: '4px',
                fontSize: '11px',
                fontWeight: 'bold',
                cursor: 'pointer'
              }}
            >
              📋 Copy Title
            </button>
            <button
              onClick={() => {
                navigator.clipboard.writeText(youtubeDescription);
                alert('Description copied to clipboard!');
              }}
              style={{
                flex: '1 1 auto',
                padding: '6px',
                backgroundColor: THEME.primary,
                color: THEME.textLight,
                border: 'none',
                borderRadius: '4px',
                fontSize: '11px',
                fontWeight: 'bold',
                cursor: 'pointer'
              }}
            >
              📋 Copy Description
            </button>
            {youtubeTags && (
              <button
                onClick={() => {
                  navigator.clipboard.writeText(youtubeTags);
                  alert('Tags copied to clipboard!');
                }}
                style={{
                  flex: '1 1 auto',
                  padding: '6px',
                  backgroundColor: THEME.primary,
                  color: THEME.textLight,
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '11px',
                  fontWeight: 'bold',
                  cursor: 'pointer'
                }}
              >
                📋 Copy Tags
              </button>
            )}
          </div>
        </div>
      )}

      {/* Actions */}
      {isExpanded && (
      <div style={{
        padding: '12px 16px',
        borderTop: `1px solid ${THEME.borderLight}`,
        display: 'flex',
        flexWrap: 'wrap',
        gap: '8px'
      }}>
        <button
          onClick={handleStitch}
          disabled={videos.length < 2 || isStitching}
          style={{
            flex: '1 1 auto',
            minWidth: '150px',
            backgroundColor: videos.length < 2 || isStitching ? THEME.textMuted : THEME.secondary,
            color: THEME.textLight,
            border: 'none',
            borderRadius: '6px',
            padding: '10px',
            fontSize: '14px',
            fontWeight: 'bold',
            cursor: videos.length < 2 || isStitching ? 'not-allowed' : 'pointer',
            transition: 'background-color 0.2s'
          }}
        >
          {isStitching ? '⏳ Stitching...' : `🎬 Stitch ${videos.length} Videos`}
        </button>
        <button
          onClick={handleGenerateThumbnail}
          disabled={!gameInstanceId || isGeneratingThumbnail}
          title={!gameInstanceId ? 'Open from Remotion Studio with a game to enable thumbnail generation' : 'Generate AI thumbnail based on video content and game characters'}
          style={{
            flex: '0 0 auto',
            backgroundColor: !gameInstanceId || isGeneratingThumbnail ? THEME.textMuted : THEME.accent,
            color: THEME.textLight,
            border: 'none',
            borderRadius: '6px',
            padding: '10px 16px',
            fontSize: '14px',
            fontWeight: 'bold',
            cursor: !gameInstanceId || isGeneratingThumbnail ? 'not-allowed' : 'pointer',
            transition: 'background-color 0.2s'
          }}
        >
          {isGeneratingThumbnail ? '⏳ Generating...' : '🖼️ Thumbnail'}
        </button>
        <button
          onClick={() => setVideos([])}
          disabled={videos.length === 0}
          style={{
            flex: '0 0 auto',
            backgroundColor: videos.length === 0 ? THEME.textMuted : THEME.danger,
            color: THEME.textLight,
            border: 'none',
            borderRadius: '6px',
            padding: '10px 16px',
            fontSize: '14px',
            fontWeight: 'bold',
            cursor: videos.length === 0 ? 'not-allowed' : 'pointer'
          }}
        >
          Clear
        </button>
      </div>
      )}
    </div>
  );
};

export default VideoList;
